/**
 * incident-dedup.ts — collapse cross-source duplicates of the same
 * real-world event into one canonical incident before counting.
 *
 * Why this exists:
 *   The incident feed aggregates news from multiple syndicators (Google
 *   News RSS, Dailyhunt, MSN, regional dailies). One real-world event —
 *   say, a single fire in Gachibowli — frequently lands as 3-4 separate
 *   items because Dailyhunt, The Hindu, and Telangana Today each
 *   republish the same story under their own headline. Counting each
 *   report as a distinct "incident" inflates per-suburb counts and
 *   breaks the safety signal: a quiet suburb with one bad event gets
 *   tagged "risky" because three news sites covered it.
 *
 *   We collapse those duplicates BEFORE the safety bucketer runs, so
 *   "one real-world event = one count" holds.
 *
 * Detection rules — all FOUR must hold for two items to merge:
 *   1. Same `suburb_id`. We never merge across suburbs because the
 *      whole point of safety is "what happened HERE".
 *   2. Same `subtype` (fire / violent-crime / theft / scam / assault).
 *      A fire and an assault in the same suburb are different events
 *      even if both happen on the same day.
 *   3. Time proximity ≤ 48 hours. The brief allows 24-48; we use 48
 *      because regional papers in our seed feed run lagged stories
 *      36+ hours after first reports. Beyond 48h the risk of merging
 *      unrelated events of the same subtype rises sharply.
 *   4. Token Jaccard similarity ≥ 0.4 on cleaned headlines.
 *      Headlines are normalised by lowercasing, stripping the trailing
 *      "- Source Name" attribution, dropping a leading "Hyderabad:" /
 *      "Watch:" / "Video:" colon-prefix, removing punctuation, and
 *      filtering out stopwords + city / generic-news tokens. Tokens
 *      shorter than 3 chars are dropped.
 *
 * Why Jaccard, why 0.4:
 *   Different sources rephrase aggressively but tend to keep the
 *   distinctive nouns (location, subject, casualty count). On the
 *   gachibowli-fire trio in our seed feed Jaccard sits ~0.55. On the
 *   genuinely-different bar attack vs arson follow-up in Melbourne CBD
 *   it stays ~0. 0.4 is the empirical sweet-spot — high enough to
 *   reject false merges, low enough to catch the 3-source duplicates
 *   we actually see.
 *
 *   We deliberately do NOT use stemming, lemmatization, or embeddings.
 *   Determinism and inspectability matter more than catching the long
 *   tail. A reviewer can look at two headlines and see why the
 *   algorithm did or didn't merge them.
 *
 * Representative selection (when multiple candidates):
 *   1. Most distinctive: headline with the most cleaned tokens (proxy
 *      for "most detailed"). A 9-token headline beats a 4-token one
 *      from the same cluster.
 *   2. Tiebreak: earliest `published_at` (first-seen wins).
 *   The representative carries through its original fields — coord,
 *   severity, subtype, source — and gains `reportCount`,
 *   `mergedSources`, `mergedIds` as extra metadata.
 *
 * Trust contract survives the merge (see mergeTrust):
 *   A cluster is as trustworthy as its best member, and as visibly
 *   disputed as its worst. The representative's trust fields are the
 *   MERGE across all members — best evidence status (disputed always
 *   wins visibility), max confidence, min (best) trust tier — so
 *   collapsing a verified police report into a news cluster never
 *   silently demotes "Verified" off the card.
 *
 * Greedy single-pass clustering:
 *   For each incident in chronological order, attach it to the FIRST
 *   existing cluster whose representative satisfies (1)-(4); else
 *   start a new cluster. O(n²) worst case, but n is small (~10-20 per
 *   city) so this is sub-millisecond.
 *
 * What this module deliberately does NOT do:
 *   - No external APIs. Pure client-side, deterministic.
 *   - No NLP heavy models. No stemming, no embeddings, no fuzzy
 *     character distance. Just bag-of-tokens Jaccard.
 *   - No heuristic extension across subtypes. A fire and an assault in
 *     the same suburb are different events.
 *   - No new datasets. The input is exactly the incident JSON the
 *     existing safety feed already loads.
 */

export interface RawIncident {
  id: string;
  suburb_id: string;
  /** Display name for the suburb. Optional because the dedup core only needs
   *  `suburb_id`, but the visible layers (bubble card, near-me panel) display
   *  this string — keeping it on the shared shape means every consumer reads
   *  identical fields off the loader without per-module casts. */
  suburb_name?: string;
  coord: [number, number];
  category: "cautionary" | "positive";
  /** Free-form subtype string from the feed. Required for matching. */
  subtype?: string;
  severity: number;
  headline: string;
  /** One-sentence "what it means" / context the engine ships alongside the
   *  headline (pg-live-v1 `body`). This is the product's differentiator —
   *  "what's happening AND what it means" — so every signal surface that has
   *  room renders it. The doorway proof already does; the in-app cards must
   *  too, or the app reads as just another feed. */
  body?: string;
  source: string;
  published_at: string;
  url?: string;
  /** Optional content format. "video" enables the YouTube thumbnail card in
   *  the bubble layer; absence/"news" is the default text-article behaviour. */
  type?: "news" | "video";
  /** Video-only: hotlinked YouTube thumbnail URL (i.ytimg.com). */
  thumb_url?: string;
  /** Video-only: YouTube video id. */
  video_id?: string;
  /** Video-only: publishing channel display name. */
  channel?: string;
  /** True for items anchored at the city centroid rather than a real suburb
   *  match — used by the `nearby_videos` parallel pool. The bubble layer
   *  must NOT render city-anchored items as map pins (they'd be a fake
   *  location); the near-me panel CAN surface them since it ranks by
   *  distance from user GPS, not by suburb. */
  is_city_anchored?: boolean;

  /* ---- Truth-engine signal contract (pg-live-v1) -------------------------
   * Present when the feed comes from the deployed pulse worker (Sydney
   * first); absent on the RSS cron feed. UI renders these rows ONLY when
   * the data carries them — never fabricate a confidence for news items. */
  /** 1 = official source · 2 = trusted media · 3 = community. */
  trust_tier?: number;
  /** Combined confidence 0–100 from the engine's governance scoring. */
  confidence_pct?: number;
  /** Engine evidence lifecycle for this signal. */
  evidence_status?: "verified" | "source_reported" | "inferred" | "disputed";
  /** Per-axis confidence (combined, source_trust, freshness, …). */
  confidence_breakdown?: Record<string, number>;
}

export interface DedupedIncident extends RawIncident {
  /** How many original reports collapsed into this representative. */
  reportCount: number;
  /** Distinct source names contributing to this cluster (deduped). */
  mergedSources: string[];
  /** Original IDs of every member of the cluster (debugging / link-out). */
  mergedIds: string[];
}

/* -------------------------------------------------------------------------- */
/*  Tunables — colocated so a reviewer can find them in one place.            */
/* -------------------------------------------------------------------------- */

const MERGE_WINDOW_MS = 48 * 60 * 60 * 1000;
const JACCARD_THRESHOLD = 0.4;
const MIN_TOKEN_LENGTH = 3;

/* -------------------------------------------------------------------------- */
/*  Stopword list — read carefully before extending.                          */
/*                                                                            */
/*  Three groups, each with a separate rationale:                             */
/*    A. English stopwords + generic news verbs ("says", "report",            */
/*       "live", "watch", "exclusive"). Every source uses them, so they       */
/*       carry no event-distinguishing signal.                                */
/*    B. City / region tokens ("hyderabad", "melbourne", "australia").        */
/*       Almost every headline mentions the city; without dropping them       */
/*       Jaccard inflates uniformly.                                          */
/*    C. Generic place / role tokens ("city", "road", "police",               */
/*       "officer"). Every crime story has them.                              */
/*                                                                            */
/*  Tokens NOT in this list (deliberately):                                   */
/*    "fire", "killed", "crash", "arson", "burglary" — these describe         */
/*    the EVENT and are exactly what we want to match on.                     */
/* -------------------------------------------------------------------------- */

const STOPWORDS = new Set<string>([
  // Group A: English + generic news verbs
  "the", "and", "but", "for", "with", "from", "this", "that",
  "those", "these", "its", "you", "your", "our", "their", "they",
  "his", "her", "them", "also", "more", "than", "into", "near",
  "over", "amid", "after", "before", "live", "watch", "video",
  "breaking", "exclusive", "update", "report", "reports", "news",
  "says", "said", "tells", "told", "day", "days", "now", "just",
  "only", "case", "amid", "some", "any", "all", "out", "got",
  // Group B: City / region tokens
  "hyderabad", "telangana", "melbourne", "victoria", "australia",
  "india", "indian", "sydney", "nsw", "bengaluru", "bangalore",
  "karnataka",
  // Group C: Generic place / role tokens
  "city", "cbd", "road", "street", "area", "suburb", "police",
  "officer", "officers", "man", "men", "woman", "women", "year",
  "years", "old", "minor", "major", "local",
]);

/* -------------------------------------------------------------------------- */
/*  Headline normalisation.                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Strip the trailing " - Source" suffix (publishers append their name)
 * and drop any leading "Hyderabad:" / "Watch:" / "Video:" colon prefix
 * that is editorial rather than informational.
 */
export function cleanHeadline(h: string): string {
  // Drop trailing "- Publisher". Only when the dash is past char 20 so we
  // don't accidentally strip a legit em-dash early in the title.
  const lastDash = h.lastIndexOf(" - ");
  let core = lastDash > 20 ? h.slice(0, lastDash) : h;
  // Drop colon-prefixed editorialisation when the prefix is short.
  // "Hyderabad: foo" → "foo"; "Watch: foo" → "foo".
  // We bound to ≤ 16 chars before the colon so we don't strip real
  // headline text that happens to contain a colon mid-sentence.
  const colon = core.indexOf(":");
  if (colon > 0 && colon < 16) core = core.slice(colon + 1);
  return core.trim();
}

/**
 * Tokenise a headline into a Set of significant tokens.
 *
 * Bag-of-words is fine for our purposes — Jaccard already ignores order.
 * We use a Set (not a multiset) so a headline that repeats a word
 * doesn't dominate the similarity score.
 */
export function tokenize(headline: string): Set<string> {
  return new Set(
    cleanHeadline(headline)
      .toLowerCase()
      // Replace anything that isn't a-z/0-9/space/dash with a space so
      // smart-quotes, em-dashes, and unicode noise become token gaps.
      .replace(/[^a-z0-9\s-]/g, " ")
      .split(/\s+/)
      .filter((t) => t.length >= MIN_TOKEN_LENGTH && !STOPWORDS.has(t))
  );
}

/* -------------------------------------------------------------------------- */
/*  Similarity                                                                */
/* -------------------------------------------------------------------------- */

/**
 * Jaccard similarity = |A ∩ B| / |A ∪ B|. Returns 0 when either set is
 * empty so we never merge an empty-token incident into anything.
 */
export function jaccard(a: Set<string>, b: Set<string>): number {
  if (a.size === 0 || b.size === 0) return 0;
  let inter = 0;
  for (const t of a) if (b.has(t)) inter++;
  const union = a.size + b.size - inter;
  return union === 0 ? 0 : inter / union;
}

/* -------------------------------------------------------------------------- */
/*  Clustering                                                                */
/* -------------------------------------------------------------------------- */

interface Cluster {
  rep: RawIncident;
  repTokens: Set<string>;
  members: RawIncident[];
}

/* -------------------------------------------------------------------------- */
/*  Trust merge — the cluster representative must not drop the contract.     */
/* -------------------------------------------------------------------------- */

/** Higher = stronger evidence. "disputed" is handled separately because it
 *  must always win visibility — a dispute on ANY member is a dispute on
 *  the cluster, never hidden behind a sibling's "verified". */
const EVIDENCE_RANK: Record<string, number> = {
  verified: 3,
  source_reported: 2,
  inferred: 1,
};

type MergedTrust = Partial<
  Pick<
    RawIncident,
    "evidence_status" | "confidence_pct" | "trust_tier" | "confidence_breakdown"
  >
>;

/**
 * Merge the engine trust fields across cluster members. Only fields the
 * data actually carries are returned — a cluster of plain news items gets
 * an empty object, never a fabricated confidence.
 */
function mergeTrust(members: ReadonlyArray<RawIncident>): MergedTrust {
  const merged: MergedTrust = {};

  // Evidence: disputed always surfaces; otherwise the strongest status.
  let best: RawIncident["evidence_status"] | undefined;
  let disputed = false;
  for (const m of members) {
    if (m.evidence_status === "disputed") disputed = true;
    else if (
      m.evidence_status &&
      (EVIDENCE_RANK[m.evidence_status] ?? 0) > (best ? EVIDENCE_RANK[best] ?? 0 : 0)
    ) {
      best = m.evidence_status;
    }
  }
  if (disputed) merged.evidence_status = "disputed";
  else if (best) merged.evidence_status = best;

  // Confidence: the cluster is as confident as its best-scored member.
  // The breakdown travels WITH that member — axes from one signal next
  // to a combined score from another would be incoherent.
  let topConfidence: RawIncident | undefined;
  for (const m of members) {
    if (typeof m.confidence_pct !== "number" || !Number.isFinite(m.confidence_pct)) continue;
    if (!topConfidence || m.confidence_pct > (topConfidence.confidence_pct as number)) {
      topConfidence = m;
    }
  }
  if (topConfidence) {
    merged.confidence_pct = topConfidence.confidence_pct;
    if (topConfidence.confidence_breakdown) {
      merged.confidence_breakdown = topConfidence.confidence_breakdown;
    }
  }

  // Tier: 1 = official source. The best (lowest) tier among members.
  let minTier: number | undefined;
  for (const m of members) {
    if (typeof m.trust_tier !== "number" || !Number.isFinite(m.trust_tier)) continue;
    if (minTier === undefined || m.trust_tier < minTier) minTier = m.trust_tier;
  }
  if (minTier !== undefined) merged.trust_tier = minTier;

  return merged;
}

/**
 * Returns the deduplicated incident list. Cross-suburb / cross-subtype
 * incidents are NEVER merged. Order of input does not affect the final
 * representative — we sort chronologically inside this function so
 * results are stable regardless of caller order.
 *
 * Performance: O(n × c) where c is the number of clusters per suburb.
 * For the seed feed (≤ ~20 incidents per city, ≤ ~3 clusters per
 * suburb) this completes in < 1 ms.
 */
export function deduplicateIncidents(
  items: ReadonlyArray<RawIncident>
): DedupedIncident[] {
  // Sort ascending by published_at; bad/missing timestamps sort to 0
  // (front), which is fine — they'll seed clusters and later items
  // attach to them based on the merge rules.
  const sorted = [...items].sort((a, b) => {
    const ta = Date.parse(a.published_at) || 0;
    const tb = Date.parse(b.published_at) || 0;
    return ta - tb;
  });

  const clusters: Cluster[] = [];

  for (const item of sorted) {
    const tokens = tokenize(item.headline);
    const t = Date.parse(item.published_at) || 0;

    // Find the first cluster (in insertion / chronological order) that
    // accepts this item. "First match wins" is good enough because the
    // suburb + subtype gate already partitions the search space tightly.
    let placed = false;
    for (const c of clusters) {
      if (c.rep.suburb_id !== item.suburb_id) continue;
      if ((c.rep.subtype ?? "") !== (item.subtype ?? "")) continue;
      const tRep = Date.parse(c.rep.published_at) || 0;
      if (Math.abs(t - tRep) > MERGE_WINDOW_MS) continue;
      if (jaccard(tokens, c.repTokens) < JACCARD_THRESHOLD) continue;

      // Match.
      c.members.push(item);
      // Upgrade representative if this item is more detailed (more
      // distinctive tokens after stopword filtering). First-seen wins
      // ties because we sorted ascending by time.
      if (tokens.size > c.repTokens.size) {
        c.rep = item;
        c.repTokens = tokens;
      }
      placed = true;
      break;
    }

    if (!placed) {
      clusters.push({ rep: item, repTokens: tokens, members: [item] });
    }
  }

  return clusters.map((c) => {
    // Distinct source names. Order matches first-seen insertion within
    // the cluster (Set preserves insertion order in ES2015+), so a
    // reviewer reading the verdict card sees sources in publishing order.
    const mergedSources = Array.from(new Set(c.members.map((m) => m.source)));
    const mergedIds = c.members.map((m) => m.id);
    return {
      ...c.rep,
      // Trust merge AFTER the rep spread — the cluster's contract is the
      // best of its members, not whichever member won the headline race.
      // mergeTrust only emits keys the data carries, so single-member
      // clusters and plain news clusters are bit-identical to the rep.
      ...mergeTrust(c.members),
      reportCount: c.members.length,
      mergedSources,
      mergedIds,
    };
  });
}

/* -------------------------------------------------------------------------- */
/*  Re-export tunables for tests / introspection.                             */
/* -------------------------------------------------------------------------- */

export const DEDUP_TUNABLES = Object.freeze({
  MERGE_WINDOW_MS,
  JACCARD_THRESHOLD,
  MIN_TOKEN_LENGTH,
});
